import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useNavigate } from "react-router-dom";

import { useBaseDatosListState } from "../../components/BaseDatosLayout";
import SuppliersManagerModal from "../../proveedores/components/SuppliersManagerModal";
import { getPurchaseItemCategoryOptions } from "../../itemsDeCompra/api/purchaseItemsApi";
import StockPageHeader from "../../../components/StockPageHeader";
import AddInsumosModal from "../components/AddInsumosModal";
import DeactivateIngredientModal from "../components/DeactivateIngredientModal";
import InsumosCardShell from "../components/InsumosCardShell";
import InsumosTable from "../components/InsumosTable";
import { getIngredients, patchIngredient } from "../api/ingredientsApi";
import {
  INGREDIENT_SECTORS,
  normalizeIngredientRow,
  normalizeIngredientSectors,
  parseYieldPercentage,
} from "../utils/ingredientFormatters";
import { errorToMessage, unwrapHttp } from "../utils/httpResponse";

const PAGE_SIZE = 25;
const INITIAL_STATE = { page: 1, nameInput: "", name: "", category: "", sector: "" };

const filterSelectSx = {
  minWidth: 150,
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.100",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 700,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "secondary.500",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
  },
};

const toolbarButtonSx = {
  height: 38,
  px: 2.4,
  borderRadius: "33px",
  textTransform: "none",
  fontSize: 12,
  fontWeight: 800,
};

function getLastValidPage(totalItems) {
  return Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
}

export default function InsumosPage() {
  const navigate = useNavigate();
  const [remembered, remember] = useBaseDatosListState("insumos", INITIAL_STATE);
  const [page, setPage] = useState(remembered.page);
  const [nameInput, setNameInput] = useState(remembered.nameInput);
  const [name, setName] = useState(remembered.name);
  const [category, setCategory] = useState(remembered.category);
  const [sector, setSector] = useState(remembered.sector);
  const [ingredients, setIngredients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [updatingSectorId, setUpdatingSectorId] = useState(null);
  const [updatingYieldId, setUpdatingYieldId] = useState(null);
  const [hidingId, setHidingId] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [addInsumosOpen, setAddInsumosOpen] = useState(false);
  const [activationResult, setActivationResult] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [suppliersOpen, setSuppliersOpen] = useState(false);

  useEffect(() => {
    remember({ page, nameInput, name, category, sector });
  }, [category, name, nameInput, page, remember, sector]);

  useEffect(() => {
    const controller = new AbortController();
    getPurchaseItemCategoryOptions({ signal: controller.signal })
      .then((response) => {
        const payload = unwrapHttp(response);
        setCategories(Array.isArray(payload) ? payload : []);
      })
      .catch(() => setCategories([]));
    return () => controller.abort();
  }, []);

  const fetchIngredients = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const response = unwrapHttp(await getIngredients({
        page,
        limit: PAGE_SIZE,
        name: name || undefined,
        category: category || undefined,
        sector: sector || undefined,
        isActive: true,
        signal,
      }));
      if (signal?.aborted) return;
      const rows = Array.isArray(response?.data) ? response.data : [];
      setIngredients(rows.map(normalizeIngredientRow).filter(Boolean));
      setTotalItems(response?.meta?.totalItems ?? rows.length);
      setTotalPages(response?.meta?.totalPages ?? (rows.length ? 1 : 0));
    } catch (requestError) {
      if (!signal?.aborted) {
        setIngredients([]);
        setError(errorToMessage(requestError));
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [category, name, page, sector]);

  useEffect(() => {
    const controller = new AbortController();
    fetchIngredients(controller.signal);
    return () => controller.abort();
  }, [fetchIngredients]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const next = nameInput.trim();
      if (next !== name) {
        setName(next);
        setPage(1);
      }
    }, 180);
    return () => clearTimeout(timeout);
  }, [name, nameInput]);

  const summary = useMemo(() => totalItems ? `Mostrando ${ingredients.length} de ${totalItems} insumos` : "Sin insumos para mostrar", [ingredients.length, totalItems]);

  async function toggleStock(ingredient) {
    if (!ingredient?.id || editingId !== ingredient.id) return;
    setTogglingId(ingredient.id);
    setError("");
    try {
      const response = await patchIngredient(ingredient.id, {
        isStockActive: !ingredient.isStockActive,
      });
      const updated = normalizeIngredientRow({
        ...unwrapHttp(response),
        activePurchaseItemCount: ingredient.activePurchaseItemCount,
      });
      setIngredients((current) => current.map((item) => item.id === ingredient.id ? updated : item));
    } catch (requestError) {
      setError(errorToMessage(requestError));
    } finally {
      setTogglingId(null);
    }
  }

  async function changeSectors(ingredient, value) {
    if (!ingredient?.id || editingId !== ingredient.id) return;
    const selected = normalizeIngredientSectors(value);
    if (!selected.length) {
      setError("El insumo debe tener al menos un sector.");
      return;
    }
    if (selected.join("|") === normalizeIngredientSectors(ingredient.sector).join("|")) return;
    setUpdatingSectorId(ingredient.id);
    setError("");
    try {
      const response = await patchIngredient(ingredient.id, { sector: selected });
      const updated = normalizeIngredientRow({
        ...unwrapHttp(response),
        activePurchaseItemCount: ingredient.activePurchaseItemCount,
      });
      setIngredients((current) => current.map((item) => item.id === ingredient.id ? updated : item));
    } catch (requestError) {
      setError(errorToMessage(requestError));
    } finally {
      setUpdatingSectorId(null);
    }
  }

  async function changeYield(ingredient, value) {
    if (!ingredient?.id || editingId !== ingredient.id) return false;
    const defaultYieldFactor = parseYieldPercentage(value);
    if (defaultYieldFactor === undefined) {
      setError("Ingresá un rendimiento entre 0% y 100%, con hasta 2 decimales.");
      return false;
    }
    if (Number(defaultYieldFactor) === Number(ingredient.defaultYieldFactor)) return true;
    setUpdatingYieldId(ingredient.id);
    setError("");
    try {
      const response = await patchIngredient(ingredient.id, { defaultYieldFactor });
      const updated = normalizeIngredientRow({
        ...unwrapHttp(response),
        activePurchaseItemCount: ingredient.activePurchaseItemCount,
      });
      setIngredients((current) => current.map((item) => item.id === ingredient.id ? updated : item));
      return true;
    } catch (requestError) {
      setError(errorToMessage(requestError));
      return false;
    } finally {
      setUpdatingYieldId(null);
    }
  }

  async function confirmRemove() {
    const ingredient = pendingRemove;
    if (!ingredient?.id) return;
    setHidingId(ingredient.id);
    setError("");
    try {
      await patchIngredient(ingredient.id, { isActive: false });
      if (editingId === ingredient.id) setEditingId(null);
      setSelectedIds((current) => current.filter((id) => id !== ingredient.id));
      const nextTotalItems = Math.max(0, totalItems - 1);
      const nextPage = Math.min(page, getLastValidPage(nextTotalItems));
      setPendingRemove(null);
      if (nextPage !== page) {
        setIngredients([]);
        setPage(nextPage);
      } else {
        await fetchIngredients();
      }
    } catch (requestError) {
      setPendingRemove(null);
      setError(errorToMessage(requestError));
    } finally {
      setHidingId(null);
    }
  }

  function setFilter(setter, value) {
    setter(value);
    setPage(1);
  }

  function openPresentations(ids) {
    const normalized = [...new Set(ids.map(Number).filter((id) => Number.isSafeInteger(id) && id > 0))];
    if (!normalized.length) return;
    navigate(`/stock/base-de-datos/insumos/presentaciones?ids=${normalized.join(",")}`);
  }

  function toggleSelected(ingredientId) {
    setSelectedIds((current) =>
      current.includes(ingredientId)
        ? current.filter((id) => id !== ingredientId)
        : [...current, ingredientId]
    );
  }

  function toggleVisible(ids, checked) {
    setSelectedIds((current) => checked
      ? [...new Set([...current, ...ids])]
      : current.filter((id) => !ids.includes(id))
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 2, flex: 1, minHeight: 0, overflow: "hidden" }}>
      <StockPageHeader title="Insumos" onBack={() => navigate("/stock")} />
      <InsumosCardShell sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} sx={{ alignItems: { xs: "stretch", lg: "center" }, justifyContent: "space-between" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { xs: "stretch", sm: "center" } }}>
              <Select size="small" displayEmpty value={category} onChange={(event) => setFilter(setCategory, event.target.value)} sx={filterSelectSx}>
                <MenuItem value="">Todas las categorías</MenuItem>
                {categories.map((item) => <MenuItem key={item.id ?? item.name} value={item.name}>{item.name}</MenuItem>)}
              </Select>
              <Select size="small" displayEmpty value={sector} onChange={(event) => setFilter(setSector, event.target.value)} sx={{ ...filterSelectSx, minWidth: 130 }}>
                <MenuItem value="">Todos los sectores</MenuItem>
                {INGREDIENT_SECTORS.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
              </Select>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { xs: "stretch", sm: "center" } }}>
              <TextField
                size="small"
                placeholder="Buscar insumo"
                value={nameInput}
                onChange={(event) => setNameInput(event.target.value)}
                sx={{
                  width: { xs: "100%", md: 220 },
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "33px",
                    bgcolor: "neutral.50",
                    color: "primary.900",
                    fontSize: 13,
                    fontWeight: 600,
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    "& fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "neutral.700",
                    opacity: 0.9,
                    fontWeight: 600,
                  },
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "primary.900", fontSize: 18 }} /></InputAdornment>,
                  endAdornment: nameInput ? <InputAdornment position="end"><ClearIcon sx={{ cursor: "pointer", fontSize: 17 }} onClick={() => { setNameInput(""); setName(""); setPage(1); }} /></InputAdornment> : null,
                }}
              />
              <Button
                variant="outlined"
                startIcon={<LocalShippingIcon />}
                onClick={() => setSuppliersOpen(true)}
                sx={{ ...toolbarButtonSx, minWidth: { xs: "100%", sm: 142 } }}
              >
                Proveedores
              </Button>
              <Button
                variant="contained"
                startIcon={selectedIds.length ? <ShoppingCartIcon /> : <AddIcon />}
                onClick={() => selectedIds.length ? openPresentations(selectedIds) : setAddInsumosOpen(true)}
                sx={{
                  ...toolbarButtonSx,
                  minWidth: { xs: "100%", sm: selectedIds.length ? 230 : 152 },
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                  "&:hover": { bgcolor: "primary.600" },
                }}
              >
                {selectedIds.length ? `Configurar presentaciones (${selectedIds.length})` : "Agregar insumos"}
              </Button>
            </Stack>
          </Stack>
          {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
          <InsumosTable
            rows={ingredients}
            sectorOptions={INGREDIENT_SECTORS}
            editingId={editingId}
            loading={loading}
            togglingId={togglingId}
            updatingSectorId={updatingSectorId}
            updatingYieldId={updatingYieldId}
            hidingId={hidingId}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            onToggleVisible={toggleVisible}
            onToggleEdit={(item) => setEditingId((current) => current === item.id ? null : item.id)}
            onToggleStock={toggleStock}
            onChangeSectors={changeSectors}
            onChangeYield={changeYield}
            onOpenPresentations={(item) => openPresentations([item.id])}
            onRequestRemove={setPendingRemove}
          />
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 700 }}>{summary}</Typography>
            <Pagination page={page} count={Math.max(totalPages, 1)} disabled={loading || totalPages <= 1} onChange={(_, value) => setPage(value)} />
          </Stack>
        </Stack>
      </InsumosCardShell>
      <AddInsumosModal
        open={addInsumosOpen}
        onClose={() => setAddInsumosOpen(false)}
        onConfirmed={(result) => {
          setAddInsumosOpen(false);
          setActivationResult(result);
          setSelectedIds(result.ingredientIds || []);
          fetchIngredients();
        }}
      />
      <Dialog open={Boolean(activationResult)} onClose={() => {}} PaperProps={{ sx: { borderRadius: "24px" } }}>
        <DialogTitle sx={{ color: "primary.900", fontWeight: 900 }}>Insumos agregados</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "neutral.700", fontWeight: 700 }}>
            Se agregaron {activationResult?.activated || 0} insumos a la licencia. Podés configurar sus presentaciones ahora o dejarlas pendientes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setActivationResult(null); setSelectedIds([]); }} sx={{ textTransform: "none", fontWeight: 800 }}>Dejar para después</Button>
          <Button
            variant="contained"
            disabled={!activationResult?.ingredientIds?.length}
            onClick={() => {
              const ids = activationResult.ingredientIds;
              setActivationResult(null);
              openPresentations(ids);
            }}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            Configurar presentaciones
          </Button>
        </DialogActions>
      </Dialog>
      <SuppliersManagerModal open={suppliersOpen} onClose={() => setSuppliersOpen(false)} />
      <DeactivateIngredientModal open={Boolean(pendingRemove)} ingredient={pendingRemove} loading={Boolean(hidingId)} onClose={() => setPendingRemove(null)} onConfirm={confirmRemove} />
    </Stack>
  );
}
